import { useSyncExternalStore } from 'react'
import {
  BookmarkModuleState,
  cloneBookmarkState,
  defaultBookmarkModuleState,
  normalizeBookmarkModuleState,
} from './model'

export const HSCMAP4_LOCAL_STORAGE_KEY = '@hscmap/hscmap/5'

let snapshot: BookmarkModuleState | undefined
const listeners = new Set<() => void>()

function readStorageRoot(raw: string | null): Record<string, any> {
  if (!raw) {
    return {}
  }
  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

export function decodeBookmarkModuleState(raw: string | null): BookmarkModuleState {
  const root = readStorageRoot(raw)
  return normalizeBookmarkModuleState(root.modules?.bookmark)
}

export function mergeBookmarkModuleState(
  raw: string | null,
  state: BookmarkModuleState,
): string {
  const root = readStorageRoot(raw)
  const modules = root.modules && typeof root.modules === 'object' && !Array.isArray(root.modules)
    ? root.modules
    : {}
  const currentBookmark = modules.bookmark
    && typeof modules.bookmark === 'object'
    && !Array.isArray(modules.bookmark)
    ? modules.bookmark
    : {}
  const currentConfig = currentBookmark.config
    && typeof currentBookmark.config === 'object'
    && !Array.isArray(currentBookmark.config)
    ? currentBookmark.config
    : {}

  return JSON.stringify({
    ...root,
    modules: {
      ...modules,
      bookmark: {
        ...currentBookmark,
        root: state.root,
        config: {
          ...currentConfig,
          walk: state.config.walk,
        },
      },
    },
  })
}

function browserStorage() {
  return typeof window === 'undefined' ? undefined : window.localStorage
}

function readSnapshot() {
  const storage = browserStorage()
  return storage
    ? decodeBookmarkModuleState(storage.getItem(HSCMAP4_LOCAL_STORAGE_KEY))
    : defaultBookmarkModuleState()
}

function emit() {
  listeners.forEach(listener => listener())
}

function getSnapshot() {
  snapshot ??= readSnapshot()
  return snapshot
}

function getServerSnapshot() {
  return defaultBookmarkModuleState()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useBookmarkState() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

export function updateBookmarkState(
  update: (draft: BookmarkModuleState) => void,
) {
  const storage = browserStorage()
  // 書き込みの直前に最新値を読み、他タブの変更を古い snapshot で消さない。
  const raw = storage?.getItem(HSCMAP4_LOCAL_STORAGE_KEY) ?? null
  const draft = cloneBookmarkState(storage ? decodeBookmarkModuleState(raw) : getSnapshot())
  update(draft)
  const next = normalizeBookmarkModuleState(draft)
  if (storage) {
    storage.setItem(HSCMAP4_LOCAL_STORAGE_KEY, mergeBookmarkModuleState(raw, next))
  }
  snapshot = next
  emit()
}

export function replaceBookmarkRoot(root: BookmarkModuleState['root']) {
  updateBookmarkState(state => {
    state.root = root
  })
}
